import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const linkBase = "whitespace-nowrap text-sm tracking-wide text-[#F7F1E8]/90 hover:text-[#F7F1E8] transition-colors";

const LINKS = [
  { to: "/", label: "Idei răzlețe", testId: "nav-writings", end: true },
  { to: "/hai-la-o-cafea", label: "Hai la o cafea", testId: "nav-talk" },
  { to: "/intrebari-anonime", label: "Întrebări anonime", testId: "nav-questions" },
  { to: "/about", label: "Despre mine", testId: "nav-about" },
];

const AtelierControls = ({ user, logout, navigate, full = false, compact = false }) =>
  user && user !== false ? (
    <div className={`flex items-center gap-3 ${full ? "w-full" : ""}`}>
      <button
        data-testid="nav-admin-btn"
        onClick={() => navigate("/admin")}
        className={`inline-flex items-center justify-center gap-2 rounded-full bg-[var(--coral)] text-xs font-medium uppercase tracking-[0.15em] text-white shadow-sm transition-colors hover:bg-[#7E0C13] active:scale-95 ${full ? "flex-1 py-3 px-4" : compact ? "px-3 py-1.5 text-[11px]" : "px-4 py-2"}`}
      >
        <PenLine className="h-3.5 w-3.5" strokeWidth={1.6} />
        Atelier
      </button>
      <button
        data-testid="nav-logout-btn"
        onClick={logout}
        className="rounded-full p-2 text-[#F7F1E8]/78 transition-colors hover:bg-[var(--tint-pink)] hover:text-[#F7F1E8]"
        aria-label="Deconectare"
      >
        <LogOut className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>
  ) : (
    <Link
      to="/login"
      data-testid="nav-login-link"
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-[color-mix(in_srgb,var(--coral)_30%,transparent)] text-xs font-medium uppercase tracking-[0.15em] text-[var(--coral)] transition-colors hover:border-[var(--coral)] hover:bg-[var(--coral)] hover:text-white ${full ? "w-full py-3 px-4" : compact ? "px-3 py-1.5 text-[11px]" : "px-4 py-2"}`}
    >
      <PenLine className="h-3.5 w-3.5" strokeWidth={1.6} />
      Atelier
    </Link>
  );

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b border-[color-mix(in_srgb,var(--accent)_55%,transparent)] bg-[#0D0907]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-4 pt-3 pb-2 sm:px-6 lg:py-5">
        <Link to="/" data-testid="nav-home-link" className="group flex min-w-0 flex-col items-start gap-0 sm:flex-row sm:items-baseline sm:gap-2 lg:flex-col lg:items-start lg:gap-1 xl:flex-row xl:items-baseline xl:gap-2">
          <span className="whitespace-nowrap font-serif text-[22px] font-medium leading-none tracking-tight text-[#F7F1E8] sm:text-2xl">La Cafea,</span>
          <span data-testid="nav-tagline" className="inline whitespace-nowrap font-sans text-[10px] font-bold uppercase tracking-[0.26em] text-[#FF4D5E] drop-shadow-[0_0_10px_rgba(255,77,94,0.35)] sm:text-xs">Fără Menajamente</span>
        </Link>

        <nav className="hidden items-center gap-x-7 lg:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkBase} data-testid={l.testId}>
              {l.label}
            </NavLink>
          ))}
          <AtelierControls user={user} logout={logout} navigate={navigate} />
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <AtelierControls user={user} logout={logout} navigate={navigate} compact />
        </div>
      </div>

      <nav
        data-testid="nav-mobile"
        className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-3 pb-3 sm:gap-x-6 lg:hidden"
      >
        {LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={`${l.testId}-mobile`}
            className={({ isActive }) =>
              `whitespace-nowrap border-b-2 pb-0.5 text-xs transition-colors sm:text-[13px] sm:tracking-wide ${isActive ? "border-[var(--accent)] text-[#F7F1E8]" : "border-transparent text-[#F7F1E8]/88 hover:text-[#F7F1E8]"}`
            }
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

export default Navbar;
